import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type KeyboardEvent,
} from "react";
import { Button } from "@datapay-by-datacom/design-system";
import { FeatureFlag, useFeatureFlagValue } from "../../feature-flags";
import { WorkflowActionBar } from "../../shared";
import {
  isSplitCostingValidationCommitReady,
  resolveSplitCostingReportState,
  type SplitCostingImportWorkflowResult,
} from "../hooks";
import {
  clientSplitCostingValidationErrors,
  clusterSplitCostingRows,
  computeSplitCostingWarnings,
  countSplitCostingTabs,
  filterSplitCostingGroupsByEmployeeCode,
  formatSplitCostingBlockedBanner,
  formatSplitCostingPathCode,
  getVisibleSplitCostingPathColumns,
  groupSplitCostingRows,
  prerequisiteSplitCostingErrors,
  splitCostingIsoToDisplayDate,
  splitCostingIssueGuidance,
  visibleSplitCostingGroups,
  type ClusteredSplitCostingRow,
  type SplitCostingClusterGroup,
  type SplitCostingClusterKind,
  type SplitCostingImportWarning,
  type SplitCostingReportTab,
  type SplitCostingReportState,
  type SplitCostingRow,
} from "../utils";
import styles from "./SplitCostingImportReport.module.css";

export const SPLIT_COSTING_CONFIRM_IMPORT_ALERT_ID =
  "split-costing-confirm-blocked";

const TABS: readonly SplitCostingReportTab[] = [
  "all",
  "errors",
  "warnings",
  "passed",
];
const TAB_LABELS: Record<SplitCostingReportTab, string> = {
  all: "All",
  errors: "Errors",
  warnings: "Warnings",
  passed: "Passed",
};

function formatLocalReadyBanner(
  readyCount: number,
  errorCount: number,
  totalCount: number,
  validationStale: boolean
): string {
  const prefix = validationStale
    ? "Changes saved locally. "
    : "Local checks are complete. ";
  const readyCopy = `${readyCount} of ${totalCount} row${
    totalCount === 1 ? "" : "s"
  } ${readyCount === 1 ? "is" : "are"} ready for server validation.`;
  if (errorCount === 0) {
    return `${prefix}${readyCopy} Select Validate to continue.`;
  }
  return `${prefix}${readyCopy} ${errorCount} row${
    errorCount === 1 ? " has" : "s have"
  } a known error. Fix the known errors, then select Validate.`;
}

export interface SplitCostingImportReportProps {
  rows: SplitCostingRow[];
  result: SplitCostingImportWorkflowResult;
  resultPhase: "validation" | "commit";
  /** Optional for callers that already computed the flag-aware warning set. */
  warnings?: readonly SplitCostingImportWarning[];
  dismissedWarningIds?: ReadonlySet<string>;
  onDismissWarning?: (warningId: string) => void;
  onEditRow?: (rowIndex: number) => void;
  validationStale?: boolean;
  canConfirmImport?: boolean;
  commitBlockedMessage?: string | null;
  onCancelImport: () => void;
  onValidate: () => void;
  onRequestCommit: () => void;
  onStartNewImport: () => void;
  isValidating?: boolean;
  isCommitting?: boolean;
  isProcessing?: boolean;
  canDownloadIssues?: boolean;
  onDownloadIssues?: () => void;
}

function uniqueWarningIds(rows: readonly ClusteredSplitCostingRow[]): string[] {
  return [
    ...new Set(rows.flatMap((row) => row.warnings.map((warning) => warning.id))),
  ];
}

function rowKey(row: ClusteredSplitCostingRow): string {
  return `${row.clusterId}:${row.fileRow}:${row.employeeCode}`;
}

export function SplitCostingImportReport({
  rows,
  result,
  resultPhase,
  warnings,
  dismissedWarningIds = new Set<string>(),
  onDismissWarning,
  onEditRow,
  validationStale = false,
  canConfirmImport,
  commitBlockedMessage,
  onCancelImport,
  onValidate,
  onRequestCommit,
  onStartNewImport,
  isValidating = false,
  isCommitting = false,
  isProcessing = false,
  canDownloadIssues = false,
  onDownloadIssues,
}: SplitCostingImportReportProps) {
  const datapayAwareValidations = useFeatureFlagValue(
    FeatureFlag.EnableSplitCostingDatapayAwareValidations
  );
  const computedWarnings = useMemo(
    () =>
      computeSplitCostingWarnings(rows, {
        skipOverAllocation: datapayAwareValidations,
      }),
    [rows, datapayAwareValidations]
  );
  const activeWarnings = warnings ?? computedWarnings;
  const prerequisiteErrors = useMemo(
    () => prerequisiteSplitCostingErrors(result),
    [result]
  );
  const hasClientValidationErrors = useMemo(
    () => clientSplitCostingValidationErrors(rows).length > 0,
    [rows]
  );
  const reportState = resolveSplitCostingReportState({
    result,
    submittedRowCount: rows.length,
    resultPhase,
    isCommitting,
    hasPrerequisiteErrors: prerequisiteErrors.length > 0,
    validationStale,
    hasClientValidationErrors,
  });
  const canConfirmCurrentValidation =
    canConfirmImport ??
    (reportState === "validation" &&
      !validationStale &&
      isSplitCostingValidationCommitReady(result, rows.length));

  const clusteredRows = useMemo(
    () =>
      clusterSplitCostingRows({
        rows,
        result,
        reportState,
        warnings: activeWarnings,
        dismissedWarningIds,
      }),
    [rows, result, reportState, activeWarnings, dismissedWarningIds]
  );
  const groups = useMemo(
    () => groupSplitCostingRows(clusteredRows),
    [clusteredRows]
  );
  const counts = useMemo(
    () => countSplitCostingTabs(clusteredRows),
    [clusteredRows]
  );
  const hasReportedBlockingErrors = counts.errors > 0;
  const validationContractIncomplete =
    reportState === "batchNotEvaluated" && prerequisiteErrors.length === 0;
  const hasBlockingErrors =
    hasReportedBlockingErrors ||
    validationContractIncomplete ||
    prerequisiteErrors.length > 0;
  const localReadyBanner = formatLocalReadyBanner(
    counts.passed,
    counts.errors,
    rows.length,
    validationStale
  );
  const firstErrorCluster =
    groups.find((group) => group.config.kind === "error")?.config.id ?? null;
  const [activeTab, setActiveTab] = useState<SplitCostingReportTab>("all");
  const [openClusters, setOpenClusters] = useState<ReadonlySet<string>>(
    () => new Set(firstErrorCluster ? [firstErrorCluster] : [])
  );
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [employeeSearch, setEmployeeSearch] = useState("");
  const trimmedEmployeeSearch = employeeSearch.trim();
  const batchKey = useMemo(() => JSON.stringify(rows), [rows]);
  const resultKey = `${reportState}:${result.total}:${result.failed}:${result.succeeded}:${result.errors
    .map((error) => `${error.index}:${error.field}:${error.error}`)
    .join("|")}`;

  useEffect(() => {
    setActiveTab("all");
    setOpenRow(null);
    setEmployeeSearch("");
    setOpenClusters(new Set(firstErrorCluster ? [firstErrorCluster] : []));
  }, [batchKey, resultKey, firstErrorCluster]);

  const visibleGroups = useMemo(
    () => visibleSplitCostingGroups(activeTab, groups),
    [activeTab, groups]
  );
  const filteredGroups = useMemo(
    () =>
      filterSplitCostingGroupsByEmployeeCode(
        visibleGroups,
        trimmedEmployeeSearch
      ),
    [visibleGroups, trimmedEmployeeSearch]
  );
  const visibleRowCount = useMemo(
    () => visibleGroups.reduce((total, group) => total + group.rows.length, 0),
    [visibleGroups]
  );
  const filteredRowCount = useMemo(
    () => filteredGroups.reduce((total, group) => total + group.rows.length, 0),
    [filteredGroups]
  );
  const clusterTotalCount = useMemo(
    () =>
      new Map(
        visibleGroups.map((group) => [group.config.id, group.rows.length])
      ),
    [visibleGroups]
  );

  const selectTab = useCallback(
    (tab: SplitCostingReportTab) => {
      setActiveTab(tab);
      setOpenRow(null);
      const first =
        tab === "errors"
          ? groups.find((group) => group.config.kind === "error")
          : visibleSplitCostingGroups(tab, groups)[0];
      setOpenClusters(new Set(first ? [first.config.id] : []));
    },
    [groups]
  );

  const onTabKeyDown = (
    event: KeyboardEvent<HTMLButtonElement>,
    tab: SplitCostingReportTab
  ) => {
    const current = TABS.indexOf(tab);
    let next: number | null = null;
    if (event.key === "ArrowRight") next = (current + 1) % TABS.length;
    if (event.key === "ArrowLeft") {
      next = (current - 1 + TABS.length) % TABS.length;
    }
    if (event.key === "Home") next = 0;
    if (event.key === "End") next = TABS.length - 1;
    if (next === null) return;
    event.preventDefault();
    const nextTab = TABS[next];
    selectTab(nextTab);
    document.getElementById(`split-costing-tab-${nextTab}`)?.focus();
  };

  const toggleCluster = (id: string) => {
    setOpenClusters((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const banner =
    reportState === "committed"
      ? `Import complete. ${result.succeeded} row${result.succeeded === 1 ? "" : "s"} imported successfully.`
      : reportState === "inconsistent"
        ? "This result is inconsistent with the all-or-nothing split costing contract. Do not assume any rows were committed; verify the company in Christmas before retrying."
        : reportState === "committing"
          ? `Importing ${rows.length} row${rows.length === 1 ? "" : "s"} as one batch. No row is confirmed as imported until the complete result returns.`
          : reportState === "localReady"
            ? localReadyBanner
            : validationContractIncomplete
            ? "Validation did not confirm the complete uploaded batch. Nothing has been committed; select Validate before importing."
            : hasBlockingErrors
              ? formatSplitCostingBlockedBanner(
                  Math.max(result.failed, counts.errors, 1),
                  rows.length
                )
              : counts.warnings > 0
                ? `${counts.warnings} row${counts.warnings === 1 ? "" : "s"} ${
                    counts.warnings === 1 ? "has" : "have"
                  } warnings. Review or dismiss them before confirming import.`
                : `${result.total} row${result.total === 1 ? "" : "s"} passed validation. Nothing has been committed yet.`;

  return (
    <div className={styles.container}>
      {prerequisiteErrors.length > 0 && (
        <div className={`${styles.banner} ${styles.bannerError}`} role="alert">
          <strong>Company prerequisite not met.</strong>
          {prerequisiteErrors.map((error) => (
            <p key={error.error}>{error.error}</p>
          ))}
          <p>{splitCostingIssueGuidance("prerequisite")}</p>
        </div>
      )}

      {prerequisiteErrors.length === 0 && (
        <div
          className={`${styles.banner} ${
            reportState === "committed"
              ? styles.bannerSuccess
              : reportState === "inconsistent" || hasBlockingErrors
                ? styles.bannerError
                : reportState === "localReady" ||
                    reportState === "committing" ||
                    counts.warnings > 0
                  ? styles.bannerWarning
                  : styles.bannerSuccess
          }`}
          role={
            reportState === "inconsistent" || hasBlockingErrors
              ? "alert"
              : "status"
          }
        >
          {banner}
        </div>
      )}

      <div
        className={styles.tabs}
        role="tablist"
        aria-label="Filter split costing report by status"
      >
        {TABS.map((tab) => (
          <button
            key={tab}
            id={`split-costing-tab-${tab}`}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            aria-controls={`split-costing-panel-${tab}`}
            tabIndex={activeTab === tab ? 0 : -1}
            className={`${styles.tab} ${
              activeTab === tab ? styles.tabActive : ""
            }`}
            onClick={() => selectTab(tab)}
            onKeyDown={(event) => onTabKeyDown(event, tab)}
          >
            {tab === "passed"
              ? reportState === "committed"
                ? "Imported"
                : reportState === "localReady"
                  ? "Ready"
                  : "Passed"
              : TAB_LABELS[tab]}{" "}
            ({counts[tab]})
          </button>
        ))}
      </div>

      {TABS.map((tab) => {
        const isActive = activeTab === tab;
        return (
          <div
            key={tab}
            id={`split-costing-panel-${tab}`}
            role="tabpanel"
            aria-labelledby={`split-costing-tab-${tab}`}
            tabIndex={isActive ? 0 : -1}
            hidden={!isActive}
            className={styles.clusters}
          >
            {isActive && (
              <>
                {visibleRowCount > 0 && (
                  <div className={styles.employeeSearchWrapper}>
                    <input
                      type="search"
                      className={styles.employeeSearch}
                      placeholder="Search employee codes…"
                      aria-label="Search employee codes"
                      value={employeeSearch}
                      onChange={(event) =>
                        setEmployeeSearch(event.target.value)
                      }
                    />
                    {trimmedEmployeeSearch !== "" && (
                      <p className={styles.employeeSearchHint}>
                        Showing {filteredRowCount} of {visibleRowCount} uploaded
                        split costing rows
                      </p>
                    )}
                  </div>
                )}

                {filteredGroups.map((group) => (
                  <Cluster
                    key={group.config.id}
                    group={group}
                    totalRowCount={
                      clusterTotalCount.get(group.config.id) ??
                      group.rows.length
                    }
                    isOpen={openClusters.has(group.config.id)}
                    onToggle={() => toggleCluster(group.config.id)}
                    openRow={openRow}
                    onToggleRow={(key) =>
                      setOpenRow((current) => (current === key ? null : key))
                    }
                    hasBlockingErrors={hasBlockingErrors}
                    reportState={reportState}
                    resultPhase={resultPhase}
                    onDismissWarning={onDismissWarning}
                    onEditRow={onEditRow}
                    isDisabled={
                      isValidating || isCommitting || isProcessing
                    }
                  />
                ))}
                {filteredGroups.length === 0 && (
                  <p className={styles.empty}>
                    {visibleRowCount > 0 && trimmedEmployeeSearch !== ""
                      ? `No employees matching "${trimmedEmployeeSearch}" in this view.`
                      : "No rows in this view."}
                  </p>
                )}
              </>
            )}
          </div>
        );
      })}

      {resultPhase === "commit" ? (
        <WorkflowActionBar
          ariaLabel="Split costing import actions"
          leadingActions={
            <Button
              variant="secondary"
              onPress={onStartNewImport}
              isDisabled={isProcessing}
            >
              Import new split costing
            </Button>
          }
          trailingActions={
            canDownloadIssues && onDownloadIssues ? (
              <Button
                variant="secondary"
                onPress={onDownloadIssues}
                isDisabled={isProcessing}
              >
                Download issues CSV
              </Button>
            ) : undefined
          }
        />
      ) : (
        <WorkflowActionBar
          ariaLabel="Split costing import actions"
          status={
            commitBlockedMessage ? (
              <div
                id={SPLIT_COSTING_CONFIRM_IMPORT_ALERT_ID}
                className={`${styles.banner} ${styles.bannerError}`}
                role="alert"
              >
                {commitBlockedMessage}
              </div>
            ) : undefined
          }
          leadingActions={
            <Button
              variant="secondary"
              onPress={onCancelImport}
              isDisabled={isValidating || isCommitting || isProcessing}
            >
              Cancel import
            </Button>
          }
          trailingActions={
            <div className={styles.footerActions}>
              {canDownloadIssues && onDownloadIssues && (
                <Button
                  variant="secondary"
                  onPress={onDownloadIssues}
                  isDisabled={isProcessing}
                >
                  Download issues CSV
                </Button>
              )}
              {!canConfirmCurrentValidation ? (
                <Button
                  variant="primary"
                  onPress={onValidate}
                  isDisabled={isValidating || isCommitting || isProcessing}
                >
                  {isValidating ? "Validating…" : "Validate"}
                </Button>
              ) : (
                <Button
                  variant="primary"
                  onPress={onRequestCommit}
                  isDisabled={isValidating || isCommitting || isProcessing}
                  aria-describedby={
                    commitBlockedMessage
                      ? SPLIT_COSTING_CONFIRM_IMPORT_ALERT_ID
                      : undefined
                  }
                >
                  {isCommitting ? "Importing…" : "Confirm import"}
                </Button>
              )}
            </div>
          }
        />
      )}
    </div>
  );
}

interface ClusterProps {
  group: SplitCostingClusterGroup;
  /** Unfiltered count for this cluster; search must not change its chip. */
  totalRowCount: number;
  isOpen: boolean;
  onToggle: () => void;
  openRow: string | null;
  onToggleRow: (key: string) => void;
  hasBlockingErrors: boolean;
  reportState: SplitCostingReportState;
  resultPhase: "validation" | "commit";
  onDismissWarning?: (warningId: string) => void;
  onEditRow?: (rowIndex: number) => void;
  isDisabled: boolean;
}

function Cluster({
  group,
  totalRowCount,
  isOpen,
  onToggle,
  openRow,
  onToggleRow,
  hasBlockingErrors,
  reportState,
  resultPhase,
  onDismissWarning,
  onEditRow,
  isDisabled,
}: ClusterProps) {
  const panelId = `split-costing-cluster-panel-${group.config.id}`;
  const title =
    group.config.id === "readyForValidation"
      ? `${totalRowCount} ready for validation`
      : group.config.id === "passed"
      ? `${totalRowCount} passed`
      : group.config.id === "imported"
        ? `${totalRowCount} imported`
      : group.config.title;
  const hint =
    group.config.id === "passed"
      ? hasBlockingErrors
        ? "These rows passed, but nothing commits until every blocking error is fixed."
        : "These rows are included only after the all-or-nothing import is confirmed."
      : group.config.hint;
  const clusterWarningIds = uniqueWarningIds(group.rows);

  return (
    <article
      className={`${styles.cluster} ${
        styles[`cluster_${group.config.kind}`]
      }`}
    >
      <button
        type="button"
        className={styles.clusterHeader}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        aria-label={`${title}, ${totalRowCount} row${
          totalRowCount === 1 ? "" : "s"
        }, ${isOpen ? "expanded" : "collapsed"}`}
      >
        <span className={styles.clusterTitleGroup}>
          <span className={styles.clusterTitle}>{title}</span>
          <span className={styles.clusterHint}>{hint}</span>
        </span>
        <span
          className={`${styles.countChip} ${
            styles[`countChip_${group.config.kind}`]
          }`}
        >
          {totalRowCount}
        </span>
      </button>
      <div id={panelId} className={styles.clusterRows} hidden={!isOpen}>
        {resultPhase === "validation" &&
          group.config.kind === "warning" &&
          onDismissWarning &&
          clusterWarningIds.length > 0 && (
            <div className={styles.clusterActions}>
              <Button
                variant="secondary"
                isDisabled={isDisabled}
                onPress={() =>
                  clusterWarningIds.forEach((id) => onDismissWarning(id))
                }
              >
                Dismiss warning{clusterWarningIds.length === 1 ? "" : "s"}
              </Button>
            </div>
          )}
        {group.rows.map((row) => {
          const key = rowKey(row);
          return (
            <ReportRow
              key={key}
              row={row}
              kind={group.config.kind}
              isOpen={openRow === key}
              onToggle={() => onToggleRow(key)}
              reportState={reportState}
              resultPhase={resultPhase}
              onDismissWarning={onDismissWarning}
              onEditRow={onEditRow}
              isDisabled={isDisabled}
            />
          );
        })}
      </div>
    </article>
  );
}

interface ReportRowProps {
  row: ClusteredSplitCostingRow;
  kind: SplitCostingClusterKind;
  isOpen: boolean;
  onToggle: () => void;
  reportState: SplitCostingReportState;
  resultPhase: "validation" | "commit";
  onDismissWarning?: (warningId: string) => void;
  onEditRow?: (rowIndex: number) => void;
  isDisabled: boolean;
}

function ReportRow({
  row,
  kind,
  isOpen,
  onToggle,
  reportState,
  resultPhase,
  onDismissWarning,
  onEditRow,
  isDisabled,
}: ReportRowProps) {
  const statusCopy =
    row.clusterId === "readyForValidation"
      ? {
          pill: "Ready",
          inline: "Passed local checks; server validation required",
          expanded:
            "This row passed local checks but still requires complete server validation before import.",
        }
      : row.clusterId === "notEvaluated"
      ? {
          pill: "Not evaluated",
          inline: "Complete-batch validation was not confirmed",
          expanded:
            "This row has not been confirmed as evaluated. Select Validate before importing.",
        }
      : row.clusterId === "committing"
        ? {
            pill: "Importing",
            inline: "Waiting for the complete import result",
            expanded:
              "This row is part of the in-progress all-or-nothing batch. It is not yet confirmed as imported.",
          }
        : row.clusterId === "imported"
          ? {
              pill: "Imported",
              inline: "Imported in the completed batch",
              expanded:
                "This row was imported as part of the completed all-or-nothing batch.",
            }
          : row.clusterId === "notConfirmed"
            ? {
                pill: "Not confirmed",
                inline: "Import outcome was not confirmed",
                expanded:
                  "Do not assume this row was imported. Verify the company in Christmas before retrying.",
              }
            : kind === "error"
              ? {
                  pill: "Error",
                  inline: row.errors[0]?.error ?? "Validation error",
                  expanded: null,
                }
              : kind === "warning"
                ? {
                    pill: "Warning",
                    inline: row.warnings[0]?.message ?? "Validation warning",
                    expanded: null,
                  }
                : {
                    pill: "Pass",
                    inline: "Passed validation",
                    expanded:
                      "This row passed validation. It will only be committed as part of the complete batch after confirmation.",
                  };
  const pill = statusCopy.pill;
  const panelId = `split-costing-row-panel-${row.clusterId}-${row.fileRow}-${row.rowIndex}`;
  const warningIds = uniqueWarningIds([row]);
  const canEdit =
    (reportState === "validation" ||
      reportState === "localReady" ||
      reportState === "batchNotEvaluated") &&
    resultPhase === "validation" &&
    onEditRow !== undefined &&
    row.row !== undefined &&
    row.issueResolution === "edit" &&
    (row.errors.length > 0 || row.warnings.length > 0);
  const canDismiss =
    reportState === "validation" &&
    resultPhase === "validation" &&
    row.errors.length === 0 &&
    onDismissWarning !== undefined &&
    warningIds.length > 0;
  const percentage = row.row?.apportionedPercentage.trim() || "—";
  const startDate =
    splitCostingIsoToDisplayDate(row.row?.startDate ?? null) || "—";
  const endDate =
    splitCostingIsoToDisplayDate(row.row?.endDate ?? null) || "—";
  const field =
    row.errors
      .map((error) => error.field)
      .filter((value): value is string => value !== null)
      .join(", ") || "—";
  const pathColumns = getVisibleSplitCostingPathColumns(
    row.row ? [row.row] : []
  );

  return (
    <div className={styles.rowItem}>
      <button
        type="button"
        className={`${styles.rowButton} ${
          isOpen ? styles.rowButtonOpen : ""
        }`}
        onClick={onToggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        aria-label={`Row ${row.fileRow}, employee ${row.employeeCode}, status ${pill}, ${isOpen ? "expanded" : "collapsed"}`}
      >
        <span aria-hidden="true">{isOpen ? "▼" : "▶"}</span>
        <span className={styles.rowNumber}>{row.fileRow}</span>
        <span className={styles.rowSummary}>
          <strong className={styles.rowEmployee}>{row.employeeCode}</strong>
          <span className={styles.rowPath} data-testid="row-path-summary">
            {row.path}
          </span>
          <span className={styles.rowReason}>{statusCopy.inline}</span>
        </span>
        <span
          className={`${styles.pill} ${styles[`pill_${kind}`]}`}
          aria-label={pill}
        >
          {pill}
        </span>
      </button>
      <div id={panelId} className={styles.accordion} hidden={!isOpen}>
          <div
            className={styles.contextTableWrapper}
            role="region"
            aria-label={`Details for row ${row.fileRow}`}
            tabIndex={0}
          >
            <table
              className={styles.contextTable}
              aria-label={`Row ${row.fileRow} split costing details`}
            >
              <thead>
                <tr>
                  <th scope="col">Employee code</th>
                  <th scope="col">Apportioned percentage</th>
                  {pathColumns.map((column) => (
                    <th key={column.level} scope="col">
                      {column.label}
                    </th>
                  ))}
                  <th scope="col">Start date</th>
                  <th scope="col">End date</th>
                  <th scope="col">Field</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>{row.employeeCode}</td>
                  <td>{percentage}</td>
                  {pathColumns.map((column) => (
                    <td key={column.level}>
                      {formatSplitCostingPathCode(row.row, column.level)}
                    </td>
                  ))}
                  <td>{startDate}</td>
                  <td>{endDate}</td>
                  <td>{field}</td>
                </tr>
              </tbody>
            </table>
          </div>
          {row.errors.map((error, index) => (
            <p
              key={`${error.field ?? "row"}:${index}`}
              className={styles.errorMessage}
            >
              <strong>Error:</strong> {error.error}
            </p>
          ))}
          {row.errors.length > 0 && (
            <p className={styles.resolutionMessage}>
              {splitCostingIssueGuidance(row.issueResolution)}
            </p>
          )}
          {row.warnings.map((warning) => (
            <p key={warning.id} className={styles.warningMessage}>
              <strong>Warning:</strong> {warning.message}
            </p>
          ))}
          {statusCopy.expanded && (
            <p
              className={
                row.clusterId === "passed" || row.clusterId === "imported"
                  ? styles.passedMessage
                  : styles.statusMessage
              }
            >
              {statusCopy.expanded}
            </p>
          )}
          {(canEdit || canDismiss) && (
            <div className={styles.rowActions}>
              {canEdit && (
                <Button
                  variant="secondary"
                  isDisabled={isDisabled}
                  onPress={() => onEditRow!(row.rowIndex)}
                >
                  Edit in UI
                </Button>
              )}
              {canDismiss && (
                <Button
                  variant="secondary"
                  isDisabled={isDisabled}
                  onPress={() =>
                    warningIds.forEach((id) => onDismissWarning!(id))
                  }
                >
                  Dismiss warning{warningIds.length === 1 ? "" : "s"}
                </Button>
              )}
            </div>
          )}
      </div>
    </div>
  );
}
